class ShippingAddressForm extends HTMLElement {
  constructor() {
    super()
    this.loadSavedValues()
    this.addEventListeners()
  }

  addEventListeners() {
    this.querySelector('#shipping-rates-form').addEventListener('submit', (event) => {
      event.preventDefault()

      const zip = this.querySelector('#zip').value.trim()
      const country = this.querySelector('#country').value
      const province = this.querySelector('#province').value

      if (!this.validateFields(zip, province)) return

      this.saveValuesToCookie(zip, country, province)
      this.fetchShippingRates(zip, country, province)
    })
  }

  validateFields(zip, province) {
    this.clearFieldErrors()

    if (!zip) {
      this.showFieldError(
        this.querySelector('#zip'),
        window.accessibilityStrings?.shippingRatesZipError ||
          'Please enter a postal code.'
      )
      return false
    }

    if (!province) {
      this.showFieldError(
        this.querySelector('#province'),
        window.accessibilityStrings?.shippingRatesProvinceError ||
          'Please select a province.'
      )
      return false
    }

    return true
  }

  showFieldError(field, message) {
    const errorElement = this.querySelector('#shipping-rates-error')

    if (field) {
      field.setAttribute('aria-invalid', 'true')
      field.setAttribute('aria-describedby', 'shipping-rates-error')
      field.focus()
    }

    if (errorElement) {
      errorElement.textContent = message
      errorElement.hidden = false
      errorElement.setAttribute('tabindex', '-1')
      errorElement.focus()
    }
  }

  clearFieldErrors() {
    const errorElement = this.querySelector('#shipping-rates-error')
    const statusElement = this.querySelector('#shipping-rates-status')

    ;[this.querySelector('#zip'), this.querySelector('#province')].forEach((field) => {
      if (!field) return
      field.removeAttribute('aria-invalid')
      field.setAttribute('aria-describedby', 'shipping-rates-error')
    })

    if (errorElement) {
      errorElement.textContent = ''
      errorElement.hidden = true
      errorElement.removeAttribute('tabindex')
    }

    if (statusElement) {
      statusElement.textContent = ''
    }
  }

  async fetchShippingRates(zip, country, province) {
    const url = `/cart/shipping_rates.json?shipping_address[zip]=${encodeURIComponent(zip)}&shipping_address[country]=${encodeURIComponent(country)}&shipping_address[province]=${encodeURIComponent(province)}`

    this.toggleLoading(true)

    try {
      const cart = await this.getCart()
      const response = await fetch(url)
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data?.error || data?.description)
      }

      const discount = this.getHighestShippingDiscount(cart.items)
      this.displayShippingRates(data.shipping_rates, discount)
    } catch (error) {
      const message =
        window.accessibilityStrings?.shippingRatesError ||
        'Unable to fetch shipping rates. Please try again.'

      this.showFieldError(this.querySelector('#zip'), message)
      this.querySelector('.inner-rates').innerHTML = ''
    } finally {
      this.toggleLoading(false)
    }
  }

  toggleLoading(isLoading) {
    const spinner = this.querySelector('.loading-overlay__spinner')

    if (isLoading) {
      this.querySelector('.inner-rates').innerHTML = ''
      spinner.classList.remove('hidden')
    } else {
      spinner.classList.add('hidden')
    }
  }

  async getCart() {
    return fetch('/cart.js').then((response) => response.json())
  }

  getHighestShippingDiscount(items) {
    let highest = 0

    items.forEach((item) => {
      const discount = item.properties?._shipping_discount
      if (discount && discount.startsWith('FS')) {
        const value = parseInt(discount.substring(2), 10)
        if (!Number.isNaN(value) && value > highest) {
          highest = value
        }
      }
    })

    return highest
  }

  displayShippingRates(rates, discount) {
    const container = this.querySelector('.inner-rates')
    const statusElement = this.querySelector('#shipping-rates-status')

    container.innerHTML = ''

    if (!rates || rates.length === 0) {
      const noResults =
        window.accessibilityStrings?.shippingRatesNoResults ||
        'No shipping rates available for this address.'

      container.innerHTML = `<p role="status">${noResults}</p>`

      if (statusElement) {
        statusElement.textContent = noResults
      }

      return
    }

    rates.forEach((rate) => {
      const price = rate.price
      const rateElement = document.createElement('div')
      rateElement.classList.add('rate')

      if (discount > 0) {
        rateElement.innerHTML = `<span><strong>${rate.name}</strong>: <s>$${price}</s> <span style="color: red;">$${Math.max(0, price - discount).toFixed(2)}</span></span>`
      } else {
        rateElement.innerHTML = `<span><strong>${rate.name}</strong>: $${price}</span>`
      }

      container.appendChild(rateElement)
    })

    if (statusElement) {
      const countMessage = (
        window.accessibilityStrings?.shippingRatesResults ||
        '[count] shipping rates found.'
      ).replace('[count]', rates.length)
      statusElement.textContent = countMessage
    }
  }

  saveValuesToCookie(zip, country, province) {
    document.cookie = `zip=${encodeURIComponent(zip)}; path=/;`
    document.cookie = `country=${encodeURIComponent(country)}; path=/;`
    document.cookie = `province=${encodeURIComponent(province)}; path=/;`
  }

  loadSavedValues() {
    const zip = this.getCookie('zip')
    const country = this.getCookie('country')
    const province = this.getCookie('province')

    if (zip && country && province) {
      this.querySelector('#zip').value = zip
      this.querySelector('#country').value = country
      this.querySelector('#province').value = province
      this.fetchShippingRates(zip, country, province)
    }
  }

  getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    return match ? decodeURIComponent(match[2]) : null
  }
}

customElements.define('shipping-rates', ShippingAddressForm)
